import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * תפעול סניף (PRD 5.9): צ'קליסטים ומשימות.
 *
 * הסימון של פריט בצ'קליסט נכתב ישירות (ה RLS מגביל לסניף), אבל
 * הפתיחה והסגירה עוברות ב RPC: הפתיחה חייבת להיות אחת ליום, והסגירה
 * בודקת שכל מה שחובה באמת סומן.
 */

export type ChecklistTemplate = {
  id: string;
  name: string;
  branch_id: string | null;
  is_active: boolean;
  /** HH:MM:SS, או null כשאין מועד. קובע גם את הסדר ביום. */
  due_time: string | null;
  sort_order: number;
};

export type ChecklistItem = {
  id: string;
  template_id: string;
  sort_order: number;
  text: string;
  requires_photo: boolean;
};

export type RunItem = {
  run_id: string;
  item_id: string;
  is_done: boolean;
  photo_path: string | null;
  note: string | null;
};

export type ChecklistRun = {
  id: string;
  template_id: string;
  branch_id: string;
  employee_id: string;
  run_date: string;
  completed_at: string | null;
  expired_at: string | null;
  notify_on_complete: boolean;
};

export type Task = {
  id: string;
  title: string;
  description: string | null;
  branch_id: string | null;
  assignee_id: string | null;
  status_id: string;
  priority_id: string | null;
  due_at: string | null;
  requires_photo: boolean;
  completion_photo_path: string | null;
  completed_at: string | null;
  expired_at: string | null;
  notify_on_complete: boolean;
  created_at: string;
  task_statuses: { name: string; is_done_state: boolean } | null;
  task_priorities: { name: string } | null;
  branches: { name: string } | null;
  employees: { first_name: string; last_name: string } | null;
};

export type ChecklistStatusRow = {
  template_id: string;
  template: string;
  /** HH:MM:SS, או null כשאין מועד לתבנית. */
  due_time: string | null;
  branch_id: string;
  branch: string;
  run_id: string | null;
  completed_at: string | null;
  expired_at: string | null;
  notify_on_complete: boolean;
  by: string | null;
  total: number;
  done: number;
};

export const OPS_KEY = ['ops'] as const;

export function useChecklistTemplates(branchId: string | null | undefined) {
  return useQuery({
    queryKey: [...OPS_KEY, 'templates', branchId],
    enabled: Boolean(branchId),
    queryFn: async (): Promise<ChecklistTemplate[]> => {
      const { data, error } = await supabase
        .from('checklist_templates')
        .select('*')
        .eq('is_active', true)
        .or(`branch_id.is.null,branch_id.eq.${branchId}`)
        // לפי מתי זה קורה ביום, ולא לפי א״ב: "סגירת סניף" הופיע
        // לפני "פתיחת סניף"
        .order('due_time', { nullsFirst: false })
        .order('sort_order')
        .order('name');
      if (error) throw error;
      return (data ?? []) as ChecklistTemplate[];
    },
  });
}

/** הריצה של היום לתבנית מסוימת, אם כבר נפתחה. */
export function useTodayRun(templateId: string | null, branchId: string | null | undefined) {
  return useQuery({
    queryKey: [...OPS_KEY, 'run', templateId, branchId],
    enabled: Boolean(templateId && branchId),
    queryFn: async () => {
      // "היום" מגיע מהשרת לפי אזור הזמן של הרשת (מיגרציה 070).
      // כשהוא חושב מהשעון של המכשיר, כל פער בין השניים מסתיים
      // במסך שמחפש ריצה בתאריך אחד בזמן שהיא נשמרה באחר — ואז
      // אי אפשר להתחיל את הצ׳קליסט בכלל.
      const [run, items] = await Promise.all([
        supabase.rpc('checklist_run_today', {
          p_template: templateId as string,
          p_branch: branchId as string,
        }),
        supabase
          .from('checklist_template_items')
          .select('*')
          .eq('template_id', templateId as string)
          .order('sort_order'),
      ]);
      if (run.error) throw run.error;
      if (items.error) throw items.error;

      const today = run.data as (ChecklistRun & { items: RunItem[] }) | null;
      const { items: runItems = [], ...runRow } = today ?? {};

      return {
        run: today ? (runRow as ChecklistRun) : null,
        items: (items.data ?? []) as ChecklistItem[],
        runItems,
      };
    },
  });
}

export function useChecklistMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: OPS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['games'] });
  };

  const open = useMutation({
    mutationFn: async (templateId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('open_checklist_run', {
        p_template: templateId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: refresh,
  });

  /** סימון פריט. upsert, כי הפריט עשוי עוד לא להיות ברשומות הריצה. */
  const setItem = useMutation({
    mutationFn: async (input: {
      runId: string;
      itemId: string;
      isDone: boolean;
      photoPath?: string | null;
      note?: string | null;
    }) => {
      const { error } = await supabase.from('checklist_run_items').upsert(
        {
          run_id: input.runId,
          item_id: input.itemId,
          is_done: input.isDone,
          ...(input.photoPath !== undefined ? { photo_path: input.photoPath } : {}),
          ...(input.note !== undefined ? { note: input.note } : {}),
        },
        { onConflict: 'run_id,item_id' },
      );
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const complete = useMutation({
    mutationFn: async (runId: string) => {
      const { error } = await supabase.rpc('complete_checklist_run', { p_run: runId });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { open, setItem, complete };
}

const TASK_SELECT = `
  id, title, description, branch_id, assignee_id, status_id, priority_id, due_at,
  requires_photo, notify_on_complete, completion_photo_path, completed_at, expired_at,
  schedule_id, created_by, created_at,
  task_statuses ( name, is_done_state ),
  task_priorities ( name ),
  branches ( name ),
  employees!tasks_assignee_id_fkey ( first_name, last_name )
`;

/** המשימות שהעובד רשאי לראות. ה RLS כבר מצמצם לסניפים ולעצמו. */
export function useTasks() {
  return useQuery({
    queryKey: [...OPS_KEY, 'tasks'],
    queryFn: async (): Promise<Task[]> => {
      const { data, error } = await supabase
        .from('tasks')
        .select(TASK_SELECT)
        .order('completed_at', { ascending: true, nullsFirst: true })
        .order('due_at', { ascending: true, nullsFirst: false })
        // שובר שוויון יציב: בלעדיו לשתי משימות עם אותו תאריך יעד
        // (או בלי תאריך בכלל) אין סדר מוגדר, ו Postgres מחזיר
        // אותן אחרת אחרי כל עדכון. התוצאה היתה שורה שקופצת
        // ממקומה ברגע שלוחצים על מתג בתוכה.
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as Task[];
    },
  });
}

export function useTaskLists() {
  return useQuery({
    queryKey: [...OPS_KEY, 'task-lists'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const [statuses, priorities] = await Promise.all([
        supabase.from('task_statuses').select('*').order('sort_order'),
        supabase.from('task_priorities').select('*').order('sort_order'),
      ]);
      if (statuses.error) throw statuses.error;
      if (priorities.error) throw priorities.error;
      return { statuses: statuses.data ?? [], priorities: priorities.data ?? [] };
    },
  });
}

export function useTaskMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: OPS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const complete = useMutation({
    mutationFn: async (input: { taskId: string; photoPath?: string | null }) => {
      const { error } = await supabase.rpc('complete_task', {
        p_task: input.taskId,
        p_photo_path: input.photoPath ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const create = useMutation({
    mutationFn: async (input: {
      title: string;
      description: string | null;
      branch_id: string | null;
      assignee_id: string | null;
      status_id: string;
      priority_id: string | null;
      due_at: string | null;
      requires_photo: boolean;
      notify_on_complete: boolean;
      created_by: string;
    }) => {
      const { error } = await supabase.from('tasks').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /**
   * עריכת משימה קיימת.
   *
   * בלי זה משימה שנוסחה לא נכון או שהוטלה על מי שלא צריך חייבה
   * למחוק ולכתוב מחדש, וזה מוחק גם את ההיסטוריה שלה.
   */
  const update = useMutation({
    mutationFn: async (input: {
      id: string;
      title: string;
      description: string | null;
      branch_id: string | null;
      assignee_id: string | null;
      priority_id: string | null;
      due_at: string | null;
      requires_photo: boolean;
      notify_on_complete?: boolean;
    }) => {
      const { id, ...values } = input;
      const { error } = await supabase.from('tasks').update(values).eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /** מתג מהיר מהרשימה, בלי לפתוח את המשימה. */
  const setNotify = useMutation({
    mutationFn: async (input: { id: string; notify: boolean }) => {
      const { error } = await supabase
        .from('tasks')
        .update({ notify_on_complete: input.notify })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tasks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { complete, create, update, setNotify, remove };
}

export function useChecklistStatus(date?: string) {
  return useQuery({
    queryKey: [...OPS_KEY, 'status', date ?? 'today'],
    queryFn: async (): Promise<ChecklistStatusRow[]> => {
      const { data, error } = await supabase.rpc('checklist_status', {
        p_date: date ?? undefined,
      });
      if (error) throw error;
      return (data as ChecklistStatusRow[] | null) ?? [];
    },
  });
}

/** כמה פריטים נשארו, בטקסט שאדם קורא. */
export function remainingLabel(done: number, total: number): string {
  if (total === 0) return 'אין משימות';
  if (done >= total) return 'הכל סומן';
  return `נשארו ${total - done} מתוך ${total}`;
}

/** האם המשימה באיחור. משימה סגורה אף פעם לא באיחור. */
export function isOverdue(task: Task): boolean {
  if (task.completed_at || task.expired_at || !task.due_at) return false;
  return new Date(task.due_at) < new Date();
}

/**
 * משימות חוזרות (מיגרציה 061).
 *
 * התבנית אומרת מה חוזר ומתי. המשימה עצמה נוצרת מהעבודה
 * המתוזמנת, ולכן שינוי בתבנית אינו משנה משימות שכבר נוצרו —
 * בדיוק כמו במשמרת קבועה.
 */
export type TaskSchedule = {
  id: string;
  title: string;
  description: string | null;
  branch_id: string | null;
  assignee_id: string | null;
  priority_id: string | null;
  requires_photo: boolean;
  notify_on_complete: boolean;
  recurrence: 'daily' | 'weekly' | 'monthly';
  weekdays: number[] | null;
  day_of_month: number | null;
  due_time: string | null;
  is_active: boolean;
  branches: { name: string } | null;
  employees: { first_name: string; last_name: string } | null;
};

export function useTaskSchedules() {
  return useQuery({
    queryKey: [...OPS_KEY, 'task-schedules'],
    queryFn: async (): Promise<TaskSchedule[]> => {
      const { data, error } = await supabase
        .from('task_schedules')
        .select(
          `id, title, description, branch_id, assignee_id, priority_id, requires_photo,
           recurrence, weekdays, day_of_month, due_time, is_active,
           branches ( name ),
           employees!task_schedules_assignee_id_fkey ( first_name, last_name )`,
        )
        .order('title');
      if (error) throw error;
      return (data ?? []) as unknown as TaskSchedule[];
    },
  });
}

export function useTaskScheduleMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: OPS_KEY });

  const save = useMutation({
    mutationFn: async (input: {
      id: string | null;
      title: string;
      description: string | null;
      branch_id: string | null;
      assignee_id: string | null;
      priority_id: string | null;
      requires_photo: boolean;
      notify_on_complete: boolean;
      recurrence: string;
      weekdays: number[] | null;
      day_of_month: number | null;
      due_time: string | null;
      created_by: string;
    }) => {
      const { id, created_by, ...values } = input;

      const { error } = id
        ? await supabase.from('task_schedules').update(values).eq('id', id)
        : await supabase.from('task_schedules').insert({ ...values, created_by });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('task_schedules').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { save, remove };
}
